import { LoadSpinner } from '@calizahq/react-components-v2'
import { Property } from '@sales-app/api/GeoLocation/GeoLocation'
import { SearchMapActions, useSearchMapContext } from '@sales-app/context/searchMap'
import { WorkspaceAction } from '@sales-app/context/workspace/workspace.actions'
import { useWorkspaceContext } from '@sales-app/context/workspace/workspace.hooks'
import { useRecommendedHomes } from '@sales-app/hooks/useRecommendedHomes/useRecommendedHomes'
import { useRecommendedProperties } from '@sales-app/hooks/useRecommendedProperties/useRecommendedProperties'
import { useUpdateEffect } from '@shared/hooks/useDidMount/useDidMount'
import GoogleMapReact, { fitBounds } from 'google-map-react'
import { Fragment, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useClickAway } from 'react-use'
import Supercluster from 'supercluster'
import useSupercluster from 'use-supercluster'

import { ReactComponent as RefreshIcon } from '@/assets/icons/refresh.svg'
import { useMobile } from '@/hooks/useMobile'

import { MapMarker } from '../MapMarker/MapMarker'
import { MapPopup } from '../MapPopup/MapPopup'
import { MapGradient, SearchMapWrapper } from './SearchMap.styles'
import {
  getQuadrantValue,
  isInCenterHalf,
  LatLngToPoint,
  transformClusterBounds,
  transformGoogleBounds,
} from './SearchMap.util'
import { SearchMapProps } from './SearchMapWithContext'

const MAP_ID = 'bd27284736917769'
const GOOGLE_MAP_ID = process.env.GOOGLE_MAPS_API_KEY
const GREY_600 = '#2E2B29'

export function SearchMap({ allowMapInteractions = true, bookablePerson }: SearchMapProps): JSX.Element | null {
  const [SearchMap, mapDispatch] = useSearchMapContext()
  const [isMapPropertiesInitialized, setIsMapPropertiesInitialized] = useState(false)
  const [isMapViewInitialized, setIsMapViewInitialized] = useState(false)
  const [{ tabs, activeTabId }, workspaceDispatch] = useWorkspaceContext()
  const activeTab = tabs.find((t) => t.bookablePersonId === activeTabId)
  const filters = activeTab?.potentialBookingFilters || { listView: true }

  const { isMobile } = useMobile()
  const { data: propertiesData, isFetched, isFetching: isFetchingProperties } = useRecommendedProperties()
  const { isFetching: isFetchingHomes } = useRecommendedHomes()

  const { amenity, beds, moveInEndDate, moveOutEndDate, moveInStartDate, moveOutStartDate, displayCity } = filters

  // const homeIds = useMemo(() => homesData?.data.homes.map(({ id }) => id), [homesData?.data.homes])

  const wrapperRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<any>(null)
  const mapsRef = useRef<any>(null)
  const { data } = propertiesData || {}

  const properties = useMemo(() => {
    return [...(data?.properties || [])]
  }, [data?.properties])

  const { neLatitude, neLongitude, swLatitude, swLongitude } = filters
  const isMapReset = !swLatitude && !swLongitude && !neLatitude && !neLongitude
  const isFetching = isFetchingHomes || isFetchingProperties

  useClickAway(wrapperRef, handleOutsideClick)

  const { clusters, supercluster } = useSupercluster<Supercluster.ClusterProperties & Property>({
    points: SearchMap.points,
    //cluster requires bounds in a different format
    bounds: transformClusterBounds(SearchMap.bounds),
    zoom: SearchMap.zoom,
    options: { radius: 75, maxZoom: 20 },
  })

  function handleMapChange({ bounds, center, size, zoom }: GoogleMapReact.ChangeEventValue) {
    const maps = mapsRef.current
    const width = size.width || wrapperRef.current?.offsetWidth || 0
    const height = size.height || wrapperRef.current?.offsetHeight || 0

    mapDispatch(SearchMapActions.setView({ center, zoom, bounds, size: { width, height } }))

    if (isFetched && maps && isMapViewInitialized && allowMapInteractions) navigate(bounds)

    if (isMapPropertiesInitialized && !isMapViewInitialized) {
      setIsMapViewInitialized(true)
    }
  }

  useEffect(() => {
    if (wrapperRef.current) {
      const width = wrapperRef.current?.offsetWidth || 0
      const height = wrapperRef.current?.offsetHeight || 0
      mapDispatch(SearchMapActions.setSize({ width, height }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapperRef.current, clusters, supercluster])

  function handleClickMap(e: MouseEvent) {
    const mapPopup = document.getElementById('map-popup')
    const clickedMapPopup = mapPopup?.contains(e.target as any)
    const clickedPin = (e.target as Element).tagName === 'circle'

    if (!clickedMapPopup && !clickedPin) {
      handleClosePopup()
    }
  }

  function handleOutsideClick() {
    !isMobile && handleClosePopup()
  }

  function handleClosePopup() {
    if (SearchMap.popup) {
      mapDispatch(SearchMapActions.closePopup())
    }
  }

  function handleMapReset(ev?: { preventDefault: () => void }) {
    ev?.preventDefault()

    setIsMapViewInitialized(false)

    workspaceDispatch(
      WorkspaceAction.updateFilters({
        neLatitude: undefined,
        neLongitude: undefined,
        swLatitude: undefined,
        swLongitude: undefined,
        currentPage: 1,
      }),
    )
  }

  function navigate(bounds: GoogleMapReact.Bounds) {
    const { ne, sw } = bounds
    if (!ne || !sw) return

    const geoLocation = {
      swLatitude: sw.lat.toString(),
      swLongitude: sw.lng.toString(),
      neLatitude: ne.lat.toString(),
      neLongitude: ne.lng.toString(),
    }
    workspaceDispatch(WorkspaceAction.updateFilters({ ...geoLocation, currentPage: 1 }))
  }

  const setMapViewFromBounds = useCallback(
    (googleMapBounds: any) => {
      if (properties && properties.length > 1) {
        const bounds = transformGoogleBounds(googleMapBounds)
        const { height, width } = SearchMap.size
        if (height && width) {
          const { center, newBounds, zoom } = fitBounds(bounds, {
            height: height - 200, // Removing bottom padding from height
            width: width - 40, // Our Marker Width is 40
          })
          mapDispatch(SearchMapActions.setView({ center, zoom, bounds: newBounds }))
        }
      } else if (properties?.length) {
        const { latitude: lat, longitude: lng } = properties[0]
        const center = { lat: Number(lat), lng: Number(lng) }
        const defaultZoom = 13
        mapDispatch(SearchMapActions.setCenter(center))
        mapDispatch(SearchMapActions.setZoom(defaultZoom))
      }
    },
    [mapDispatch, SearchMap.size, properties],
  )

  const hydrateMap = useCallback(() => {
    const maps = mapsRef.current
    if (maps && properties) {
      const googleMapBounds = new maps.LatLngBounds()

      properties?.forEach((p) => {
        googleMapBounds.extend(new maps.LatLng(Number(p.latitude), Number(p.longitude)))
      })

      isMapReset && setMapViewFromBounds(googleMapBounds)
      mapDispatch(SearchMapActions.setPoints(properties || []))
      setIsMapPropertiesInitialized(true)
    }
  }, [isMapReset, mapDispatch, properties, setMapViewFromBounds])

  useUpdateEffect(() => {
    // if filters change and not geoLocation search, reinitilize map to prevent map change, which forces geoLocation search
    if (isMapReset) {
      setIsMapViewInitialized(false)
    }
  }, [amenity, beds, moveInEndDate, moveOutEndDate, moveInStartDate, moveOutStartDate, displayCity, isMapReset])

  useEffect(() => {
    hydrateMap()
  }, [hydrateMap])

  return (
    <Fragment>
      <SearchMapWrapper data-testid="search-map-wrapper" onClick={handleClickMap} ref={wrapperRef}>
        <div className="absolute bottom-0 left-0 right-0 top-0 h-full w-full overflow-hidden rounded-2xl">
          <MapGradient />

          <GoogleMapReact
            bootstrapURLKeys={{ key: GOOGLE_MAP_ID || 'key-not-found' }}
            center={SearchMap.center}
            onChange={handleMapChange}
            onGoogleApiLoaded={({ map, maps }) => {
              mapRef.current = map
              mapsRef.current = maps
              hydrateMap()
            }}
            onZoomAnimationStart={() => handleClosePopup()}
            options={{
              scrollwheel: !SearchMap.isMouseOverPopup,
              disableDoubleClickZoom: !!SearchMap.popup,
              fullscreenControl: false,
              clickableIcons: false,
              gestureHandling: 'greedy',
              mapId: MAP_ID,
              zoomControlOptions: {
                position: mapsRef.current?.ControlPosition.TOP_RIGHT,
              },
              styles: undefined,
              ...({ mapId: MAP_ID } as any),
            }}
            yesIWantToUseGoogleMapApiInternals={true}
            zoom={SearchMap.zoom}
          >
            {clusters.map((cluster, index) => {
              const [lng, lat] = cluster.geometry.coordinates
              const { cluster: isCluster, point_count: pointCount } = cluster.properties
              const center = {
                //these can arrive as strings
                lat: Number(lat),
                lng: Number(lng),
              }
              //this whole setup is a cluster...
              if (isCluster && cluster?.id && cluster.properties.cluster === true) {
                return (
                  <MapMarker
                    // isFavorite={isClusteredFavorited}
                    // isHovered={isClusterHovered}
                    key={cluster.id}
                    lat={lat}
                    lng={lng}
                    onClick={() => {
                      //Zoom over there
                      const expansionZoom = Math.min(
                        supercluster?.getClusterExpansionZoom(Number(cluster.id)) || 20,
                        20,
                      )
                      mapDispatch(SearchMapActions.setZoom(expansionZoom))
                      mapDispatch(SearchMapActions.setCenter(center))
                    }}
                    pointCount={pointCount}
                  />
                )
              } else {
                const isMarkerPopupOpen = SearchMap.popup?.id === cluster.properties.id

                return (
                  <MapMarker
                    data-testid="property-map-marker"
                    isActive={isMarkerPopupOpen}
                    // isFavorite={isFavorite}
                    // isHovered={isMarkerHomeHovered}
                    key={`${cluster.properties.id}-${index}`}
                    lat={lat}
                    lng={lng}
                    onClick={() => {
                      cluster.properties.mapProperty = {
                        quadrant: getQuadrantValue(SearchMap.bounds, SearchMap.center, center, mapsRef.current),
                        inCenterHalf: isInCenterHalf(SearchMap.bounds, SearchMap.center, center, mapsRef.current),
                        position: LatLngToPoint(mapRef.current, {
                          lat: Number(lat),
                          lng: Number(lng),
                        }),
                      }
                      mapDispatch(SearchMapActions.openPopup(cluster.properties as Property))
                    }}
                  />
                )
              }
            })}
          </GoogleMapReact>
          <MapPopup bookablePerson={bookablePerson} />
          {!isMobile && (
            <button
              className="absolute top-30 z-20 rounded-lg bg-white p-2"
              onClick={handleMapReset}
              style={{ right: '22px' }}
            >
              <RefreshIcon color={GREY_600} height={24} width={24} />
            </button>
          )}

          {isFetching && (
            <div className="absolute right-4 top-4 z-20 rounded-lg bg-white p-2 md:right-6 md:top-44">
              <LoadSpinner color={GREY_600} size={1.5} />
            </div>
          )}
        </div>
      </SearchMapWrapper>
    </Fragment>
  )
}
